import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  SimpleGrid,
  Flex,
  Icon,
  Text,
  useColorModeValue,
  Spinner,
  IconButton,
} from "@chakra-ui/react";
import { MdAdd, MdEdit, MdDelete, MdArrowBack, MdSave } from "react-icons/md";
import { Table, Popconfirm, Pagination } from "antd"; // Using Ant Design Table as seen in Plan view
import { Controller } from "Controller/Controller";
import { Alert } from "components/Alert/Alert";
import Card from "components/card/Card";

// Components
import RewardPlanForm from "./components/RewardPlanForm";
import RewardPlanRules from "./components/RewardPlanRules";
import ServiceRules from "./components/ServiceRules";

const Reward = () => {
  // Styles
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const accentColor = "#1677ff";

  // States
  const [viewMode, setViewMode] = useState("list"); // 'list', 'create', 'edit'
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [rewardPlans, setRewardPlans] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });

  // Editing State
  const [editingId, setEditingId] = useState(null);

  // Form State
  const [rewardPlanName, setRewardPlanName] = useState("");
  const [rewardPlanDescription, setRewardPlanDescription] = useState("");
  const [rewardRatio, setRewardRatio] = useState(1.0);
  const [selectedPlans, setSelectedPlans] = useState([]);

  // Membership Plans Data (for the modal)
  const [allPlans, setAllPlans] = useState([]);
  const [totalPlans, setTotalPlans] = useState(0);
  const [currentModalPage, setCurrentModalPage] = useState(1);
  const [modalLoading, setModalLoading] = useState(false);

  // Service Data (for the modal)
  const [allServices, setAllServices] = useState([]);
  const [totalServices, setTotalServices] = useState(0);
  const [currentServiceModalPage, setCurrentServiceModalPage] = useState(1);
  const [serviceModalLoading, setServiceModalLoading] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [globalServicePoints, setGlobalServicePoints] = useState(0);

  // Search Filters
  const [membershipPlanSearch, setMembershipPlanSearch] = useState("");
  const [serviceSearch, setServiceSearch] = useState("");

  // --- 1. Initial Load & List Data ---
  const fetchRewardPlans = useCallback(
    async (page = 1, currentFilters = null) => {
      setLoading(true);
      const filters = currentFilters || {
        membershipPlanSearch,
        serviceSearch,
      };

      try {
        const officeId = localStorage.getItem("selectedOffice");
        const res = await Controller.GetRewardPlans(
          officeId,
          null, // customerId
          page,
          null, // membership_plan (ID)
          null, // service (ID)
          filters.membershipPlanSearch,
          filters.serviceSearch
        );
        if (res.status < 300) {
          if (Array.isArray(res.json)) {
            setRewardPlans(res.json);
            setPagination((prev) => ({
              ...prev,
              current: page,
              total: res.json.length,
            }));
          } else if (res.json && Array.isArray(res.json.results)) {
            setRewardPlans(res.json.results);
            setPagination((prev) => ({
              ...prev,
              current: page,
              total: res.json.count || 0,
            }));
          } else {
            console.warn("Unexpected reward plans format", res.json);
            setRewardPlans([]);
          }
        } else {
          Alert.openNotification("Failed to load reward plans.", "error");
        }
      } catch (error) {
        console.error(error);
        Alert.openNotification(
          "An error occurred while loading plans.",
          "error"
        );
      }
      setLoading(false);
    },
    [membershipPlanSearch, serviceSearch]
  );

  useEffect(() => {
    fetchMembershipPlans(1); // Pre-fetch first page of membership plans
    fetchServices(); // Pre-fetch services
  }, []);

  // Live Search with Debounce
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchRewardPlans(1);
    }, 500); // 500ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [membershipPlanSearch, serviceSearch, fetchRewardPlans]);

  // --- 2. Membership Plans (for selection) ---
  const fetchMembershipPlans = async (page) => {
    setModalLoading(true);
    try {
      const res = await Controller.GetAllMembership(page);
      if (res.status < 250 && res.json.results) {
        setAllPlans(res.json.results);
        setTotalPlans(res.json.count || 0);
        setCurrentModalPage(page);
      }
    } catch (error) {
      console.error("Failed to fetch membership plans", error);
    } finally {
      setModalLoading(false);
    }
  };

  // --- 3. Services (for selection) ---
  const fetchServices = async () => {
    setServiceModalLoading(true);
    try {
      const res = await Controller.GetServiceNP(); // Use standard service list endpoint
      if (res.status < 250 && Array.isArray(res.json)) {
        setAllServices(res.json);
        setTotalServices(res.json.length);
        setCurrentServiceModalPage(1);
      }
    } catch (error) {
      console.error("Failed to fetch services", error);
    } finally {
      setServiceModalLoading(false);
    }
  };

  // --- 3. CRUD Actions ---

  const handleCreateNew = () => {
    setEditingId(null);
    setRewardPlanName("");
    setRewardPlanDescription("");
    setRewardRatio(1.0);
    setSelectedPlans([]);
    setSelectedServices([]);
    setGlobalServicePoints(0);
    setViewMode("create");
  };

  const handleEdit = async (plan) => {
    if (loading || isSaving) return;
    setEditingId(plan.id);
    setSelectedServices([]); // Initialize local state for services
    setGlobalServicePoints(0);
    setViewMode("edit");
    setLoading(true);

    try {
      // Fetch full detail to get rules
      const res = await Controller.GetRewardPlanDetail(plan.id);
      if (res.status < 300) {
        const data = res.json;
        setRewardPlanName(data.name);
        setRewardPlanDescription(data.description || "");

        // Reconstruct logic from API rules
        // API: rules: [{ membership_plan: ID, multiplier: 1.5, ... }]
        const rules = data.rules || [];
        if (rules.length > 0) {
          // Infer ratio from the first rule (assuming global consistency per requirement)
          setRewardRatio(rules[0].multiplier);

          // We need the full plan objects for 'selectedPlans' state (id, name, etc.)
          // Fetch full details for each membership plan if only ID is returned
          const mappedPlansPromises = rules.map(async (r) => {
            // Check if r.membership_plan is an object or ID
            if (
              typeof r.membership_plan === "object" &&
              r.membership_plan !== null
            ) {
              return r.membership_plan;
            }

            // membership_plan is just an ID, fetch the full details
            const planId = r.membership_plan;

            // First, try to find in allPlans (already fetched)
            const existingPlan = allPlans.find((p) => p.id === planId);
            if (existingPlan) {
              return existingPlan;
            }

            // If not found in allPlans, fetch from API
            try {
              const detailRes = await Controller.GetMembershipDetail(planId);
              if (detailRes.status < 300 && detailRes.json) {
                return {
                  id: detailRes.json.id,
                  name: detailRes.json.name,
                  cost: detailRes.json.cost,
                  member_count: detailRes.json.member_count || 0,
                };
              }
            } catch (fetchErr) {
              console.error(
                `Failed to fetch membership plan ${planId}:`,
                fetchErr
              );
            }

            // Fallback if fetch fails
            return {
              id: planId,
              name: `Plan #${planId}`,
              cost: "N/A",
              member_count: 0,
            };
          });

          const mappedPlans = await Promise.all(mappedPlansPromises);
          setSelectedPlans(mappedPlans);
        } else {
          setRewardRatio(1.0);
          setSelectedPlans([]);
        }

        // --- Handle Service Rules ---
        const serviceRules = data.service_rules || [];
        if (serviceRules.length > 0) {
          // Infer global points from first rule
          setGlobalServicePoints(serviceRules[0].points);

          const mappedServicesPromises = serviceRules.map(async (sr) => {
            const serviceId = sr.service;
            // Use Sr.service to match against allServices
            // Note: Some endpoints return service name directly or we find it in our list
            const existingService = allServices.find((s) => s.id === serviceId);
            if (existingService) {
              return existingService;
            }
            return {
              id: serviceId,
              name: sr.service_name || `Service #${serviceId}`,
            };
          });

          const mappedServices = await Promise.all(mappedServicesPromises);
          setSelectedServices(mappedServices);
        } else {
          setGlobalServicePoints(0);
          setSelectedServices([]);
        }
      }
    } catch (err) {
      console.error(err);
      Alert.openNotification("Failed to load plan details", "error");
      setViewMode("list");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (loading || isSaving) return;
    setLoading(true);
    try {
      const res = await Controller.DeleteRewardPlan(id);
      if (res.status === 204 || res.status < 300) {
        Alert.openNotification("Plan deleted successfully", "success");
        fetchRewardPlans();
      } else {
        Alert.openNotification("Failed to delete plan", "error");
      }
    } catch (e) {
      Alert.openNotification("Error deleting plan", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    const officeId = localStorage.getItem("selectedOffice");

    // Construct Rules payload
    const rulesPayload = selectedPlans.map((p) => ({
      membership_plan: p.id,
      multiplier: parseFloat(rewardRatio) || 1,
    }));

    const serviceRulesPayload = selectedServices.map((s) => ({
      service: s.id,
      points: parseInt(globalServicePoints) || 0,
    }));

    const payload = {
      office: officeId,
      name: rewardPlanName,
      description: rewardPlanDescription,
      is_active: true,
      rules: rulesPayload,
      service_rules: serviceRulesPayload,
    };

    try {
      let res;
      if (viewMode === "create") {
        res = await Controller.CreateRewardPlan(payload);
      } else {
        // Edit
        res = await Controller.UpdateRewardPlan(editingId, payload);
      }

      if (res.status < 300) {
        Alert.openNotification(
          `Reward Plan ${
            viewMode === "create" ? "created" : "updated"
          } successfully!`,
          "success"
        );
        setViewMode("list");
        fetchRewardPlans();
      } else {
        Alert.openNotification(res.message || "Operation failed", "error");
      }
    } catch (error) {
      console.error("Save error", error);
      Alert.openNotification("An error occurred while saving.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // --- 4. Renderers ---

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <Text fontWeight="600" color={textColor}>
          {text}
        </Text>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Actions",
      align: "center",
      key: "action",
      render: (_, record) => (
        <Flex gap="10px" display={"flex"} justifyContent={"center"}>
          <IconButton
            icon={<MdEdit />}
            size="sm"
            onClick={() => handleEdit(record)}
            aria-label="Edit"
          />
          <Popconfirm
            title="Are you sure delete this reward plan?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <IconButton
              icon={<MdDelete />}
              size="sm"
              colorScheme="red"
              aria-label="Delete"
            />
          </Popconfirm>
        </Flex>
      ),
    },
  ];

  if (viewMode === "list") {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Card mb="20px">
          <Flex justify="space-between" align="center" mb="20px">
            <Text fontSize="22px" fontWeight="700" color={textColor}>
              Reward Plans
            </Text>
            <Button
              bg={accentColor}
              color="white"
              _hover={{ bg: "#0870ad" }}
              leftIcon={<Icon as={MdAdd} />}
              onClick={handleCreateNew}
            >
              Create New Plan
            </Button>
          </Flex>

          {/* Search Filters */}
          <Flex gap="10px" mb="20px" align="center" w="50%">
            <Box flex="1">
              <Text fontSize="sm" mb="5px" fontWeight="500">
                Membership Plan Name
              </Text>
              <input
                placeholder="Search"
                value={membershipPlanSearch}
                onChange={(e) => setMembershipPlanSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "1px solid #E2E8F0",
                  outline: "none",
                }}
              />
            </Box>
            <Box flex="1">
              <Text fontSize="sm" mb="5px" fontWeight="500">
                Service Name
              </Text>
              <input
                placeholder="Search"
                value={serviceSearch}
                onChange={(e) => setServiceSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "1px solid #E2E8F0",
                  outline: "none",
                }}
              />
            </Box>
          </Flex>

          {loading ? (
            <Flex justify="center" p={5}>
              <Spinner />
            </Flex>
          ) : (
            <Table
              dataSource={rewardPlans}
              columns={columns}
              rowKey="id"
              pagination={false}
            />
          )}
          {pagination.total > pagination.pageSize && (
            <Flex justify="center" align="center" py="15px" w="100%">
              <Pagination
                current={pagination.current}
                total={pagination.total}
                pageSize={pagination.pageSize}
                onChange={(p) => fetchRewardPlans(p)}
                showSizeChanger={false}
                size="small"
              />
            </Flex>
          )}
        </Card>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Flex mb="20px" align="center">
        <IconButton
          icon={<MdArrowBack />}
          onClick={() => setViewMode("list")}
          mr="10px"
          variant="ghost"
        />
        <Text fontSize="2xl" fontWeight="bold" color={textColor}>
          {viewMode === "create" ? "Create Reward Plan" : "Edit Reward Plan"}
        </Text>
      </Flex>

      <SimpleGrid columns={1} gap="20px">
        <RewardPlanForm
          name={rewardPlanName}
          setName={setRewardPlanName}
          description={rewardPlanDescription}
          setDescription={setRewardPlanDescription}
        />

        <SimpleGrid columns={{ base: 1, xl: 2 }} gap="20px">
          <RewardPlanRules
            allPlans={allPlans}
            totalPlans={totalPlans}
            currentPage={currentModalPage}
            onPageChange={fetchMembershipPlans}
            selectedPlans={selectedPlans}
            setSelectedPlans={setSelectedPlans}
            loading={modalLoading}
            rewardRatio={rewardRatio}
            setRewardRatio={setRewardRatio}
            onSave={handleSave}
            isSaveDisabled={
              !rewardPlanName ||
              (selectedPlans.length === 0 && selectedServices.length === 0) ||
              (selectedPlans.length > 0 &&
                (!rewardRatio || parseFloat(rewardRatio) === 0)) ||
              (selectedServices.length > 0 &&
                (!globalServicePoints || parseInt(globalServicePoints) === 0))
            }
          />

          <ServiceRules
            allServices={allServices}
            totalServices={totalServices}
            currentPage={currentServiceModalPage}
            onPageChange={(page) => setCurrentServiceModalPage(page)}
            selectedServices={selectedServices}
            setSelectedServices={setSelectedServices}
            globalServicePoints={globalServicePoints}
            setGlobalServicePoints={setGlobalServicePoints}
            loading={serviceModalLoading}
            onSave={handleSave}
            isSaveDisabled={
              !rewardPlanName ||
              (selectedPlans.length === 0 && selectedServices.length === 0) ||
              (selectedPlans.length > 0 &&
                (!rewardRatio || parseFloat(rewardRatio) === 0)) ||
              (selectedServices.length > 0 &&
                (!globalServicePoints || parseInt(globalServicePoints) === 0))
            }
          />
        </SimpleGrid>

        <Flex justify="flex-end" mt="20px">
          <Button
            bg={accentColor}
            color="white"
            _hover={{ bg: "#0870ad" }}
            _active={{ bg: "#076094" }}
            fontWeight="500"
            isLoading={isSaving}
            leftIcon={!isSaving && <Icon as={MdSave} color="white" />}
            onClick={handleSave}
            isDisabled={
              !rewardPlanName ||
              (selectedPlans.length === 0 && selectedServices.length === 0) ||
              (selectedPlans.length > 0 &&
                (!rewardRatio || parseFloat(rewardRatio) === 0)) ||
              (selectedServices.length > 0 &&
                (!globalServicePoints || parseInt(globalServicePoints) === 0))
            }
          >
            Save Reward Plan
          </Button>
        </Flex>
      </SimpleGrid>

      {/* Full-module blocking overlay during save */}
      {isSaving && (
        <Box
          position="fixed"
          top="0"
          left="0"
          width="100vw"
          height="100vh"
          bg="rgba(255, 255, 255, 0.7)"
          zIndex="9999"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          cursor="not-allowed"
          pointerEvents="all"
        >
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color={accentColor}
            size="xl"
            mb="20px"
          />
          <Text fontSize="lg" fontWeight="bold" color={textColor}>
            Saving Reward Plan...
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default Reward;
